// src/Components/MEICSupportStats.js

// ─────────────────────────────────────────────
// 1) Canonical schedules in Eastern Time (ET)
// ─────────────────────────────────────────────

const regimeSchedulesEt = {
  0: ['09:33', '9:45', '10:00', '10:30', '10:45', '14:15'],
  1: ['09:33', '9:45', '10:00', '10:30', '10:45', '14:15'],
  2: ['12:30', '12:45', '13:00', '13:15', '13:45', '14:00'],
  3: ['13:00', '13:30', '14:00', '14:15', '14:30', '14:45']
};

export function getRegimeScheduleEt(regime) {
  return regimeSchedulesEt?.[regime] ?? [];
}

// ─────────────────────────────────────────────
// 2) Timezone offset helper (no external libs)
//    Get offset (in minutes) for a given IANA
//    timezone on a specific Date.
// ─────────────────────────────────────────────

function getOffsetMinutesForZone(timeZone, date) {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = dtf.formatToParts(date);
  const map = {};

  for (const { type, value } of parts) {
    if (type !== 'literal') {
      map[type] = value;
    }
  }

  // This constructs the *UTC* timestamp of that local time
  const asUTC = Date.UTC(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );

  // Offset (in minutes) between local TZ and UTC
  const diffMs = asUTC - date.getTime();
  return diffMs / 60000;
}

// ─────────────────────────────────────────────
// 3) Convert ET schedule → local schedule
//    tradingDate should be a YYYY-MM-DD string
//    in *market date* (ET).
// ─────────────────────────────────────────────

export function getLocalScheduleForRegime({
  regime,
  tradingDate,   // 'YYYY-MM-DD' (ET market date) – optional, will fall back to today
  userTimeZone,  // IANA string, e.g. 'America/Los_Angeles' – optional, will use browser TZ
}) {
  const etTimes = getRegimeScheduleEt(regime);
  if (!etTimes.length) return [];

  const userTz =
    userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';

  // Use midday UTC of trading date as our reference for offsets (stable for DST)
  let baseDate;
  if (tradingDate) {
    // Expect tradingDate: 'YYYY-MM-DD'
    baseDate = new Date(`${tradingDate}T12:00:00Z`);
  } else {
    // Fallback: today (still fine for offsets)
    baseDate = new Date();
  }

  const etOffsetMinutes = getOffsetMinutesForZone('America/New_York', baseDate);
  const localOffsetMinutes = getOffsetMinutesForZone(userTz, baseDate);
  const deltaMinutes = localOffsetMinutes - etOffsetMinutes;

  return etTimes.map((etStr) => {
    const [hh, mm] = etStr.split(':').map((n) => parseInt(n, 10) || 0);
    const etMinutes = hh * 60 + mm;

    let localMinutes = etMinutes + deltaMinutes;

    // Wrap around 0–1440
    const minutesInDay = 24 * 60;
    while (localMinutes < 0) localMinutes += minutesInDay;
    while (localMinutes >= minutesInDay) localMinutes -= minutesInDay;

    const localH = Math.floor(localMinutes / 60);
    const localM = localMinutes % 60;

    const local24 =
      String(localH).padStart(2, '0') + ':' + String(localM).padStart(2, '0');

    const localAmPm = formatLocalAmPm(localH, localM);

    return {
      et: etStr,
      local24,
      localLabel: localAmPm,
    };
  });
}

function formatLocalAmPm(h, m) {
  const suffix = h >= 12 ? 'pm' : 'am';
  const hour12 = ((h + 11) % 12) + 1;
  const mins = String(m).padStart(2, '0');
  return `${hour12}:${mins} ${suffix}`;
}

// ─────────────────────────────────────────────
// 4) Header-friendly summary strings
// ─────────────────────────────────────────────

export function getHeaderScheduleSummary({
  regime,
  tradingDate,
  userTimeZone,
  maxCount = 4, // limit for UI, we'll show "…" if more
}) {
  const etTimes = getRegimeScheduleEt(regime);
  if (!etTimes.length) {
    return {
      etLabel: '',
      localLabel: '',
    };
  }

  const localSchedule = getLocalScheduleForRegime({
    regime,
    tradingDate,
    userTimeZone,
  });

  const etDisplay =
    etTimes.length > maxCount
      ? `${etTimes.slice(0, maxCount).join(', ')}…`
      : etTimes.join(', ');

  const localDisplay = localSchedule
  .map((t) => t.localLabel)
  .join(', ');

  return {
    // etLabel: `ET entries: ${etDisplay}`,
    localLabel: `Position Entry Times: ${localDisplay}`,
  };
}

// ─────────────────────────────────────────────
// 5) Trade-level statistics from today's trades
// ─────────────────────────────────────────────

function toSafeNumber(value) {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Compute MEIC statistics from an array of trades.
 *
 * Expected trade shape (fields you are already using in MEICWidget):
 *  - status: 'OPEN' | 'CLOSED' (case-insensitive)
 *  - outcome: 'STOP' | 'EXP' | 'EXPIRE' | 'EXPIRATION' | ...
 *  - pnlDollars: number (can be string, we coerce)
 */
export function computeMeicTradeStats(trades) {
  const result = {
    totalUnrealizedPnlDollars: 0,
    totalRealizedPnlDollars: 0,

    numWins: 0,
    numLosses: 0,
    numStops: 0,
    numExpires: 0,
    totalTradesClosed: 0,

    avgWinPnlDollars: 0,
    avgLossPnlDollars: 0,
    winPct: 0,
  };

  if (!Array.isArray(trades) || trades.length === 0) {
    return result;
  }

  let winSum = 0;
  let lossSum = 0;

  for (const t of trades) {
    if (!t) continue;

    const status = String(t.status || '').trim().toUpperCase();
    const outcome = String(t.outcome || '').trim().toUpperCase();
    const pnl = toSafeNumber(t.pnlDollars);

    if (status === 'OPEN') {
      // Unrealized PnL: open positions only
      result.totalUnrealizedPnlDollars += pnl;
    } else if (status === 'CLOSED') {
      // Realized PnL: closed positions only
      result.totalRealizedPnlDollars += pnl;
      result.totalTradesClosed += 1;

      if (pnl > 0) {
        result.numWins += 1;
        winSum += pnl;
      } else if (pnl < 0) {
        result.numLosses += 1;
        lossSum += pnl;
      }
    }

    // Stops / Expires based on outcome
    if (outcome === 'STOP') {
      result.numStops += 1;
    } else if (
      outcome === 'EXP' ||
      outcome === 'EXPIRE' ||
      outcome === 'EXPIRATION'
    ) {
      result.numExpires += 1;
    }
  }

  // Averages
  if (result.numWins > 0) {
    result.avgWinPnlDollars = winSum / result.numWins;
  }
  if (result.numLosses > 0) {
    result.avgLossPnlDollars = lossSum / result.numLosses;
  }

  // Win percentage: wins vs stops only (no separate "loss" bucket)
  const decided = result.numWins + result.numStops;
  if (decided > 0) {
    result.winPct = (result.numWins / decided) * 100;
  }


  return result;
}